"use client";

import { c as _c } from "react/compiler-runtime";
import { useMemo } from "react";
import Image from "next/image";
import { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import { parseIpfsUrl } from "@/helpers/Helpers";
import { getScaledImageUri, ImageScale } from "@/helpers/image.helpers";
import { getFileInfoFromUrl } from "@/helpers/file.helpers";
import { AdditionalMedia, MemesSubmissionAdditionalInfoKey } from "@/components/waves/memes/submission/types/OperationalData";
const MAX_MEDIA = 4;
const VIDEO_EXTENSIONS = new Set(["mp4", "mov", "m4v", "webm", "ogv"]);
const emptyAdditionalMedia: AdditionalMedia = {
  artist_profile_media: [],
  artwork_commentary_media: [],
  preview_image: ""
};
const parseAdditionalMedia = (rawValue?: string): AdditionalMedia => {
  if (!rawValue) {
    return emptyAdditionalMedia;
  }
  try {
    const parsed = JSON.parse(rawValue) as Partial<AdditionalMedia> | null;
    if (!parsed || typeof parsed !== "object") {
      return emptyAdditionalMedia;
    }
    return {
      artist_profile_media: Array.isArray(parsed.artist_profile_media) ? parsed.artist_profile_media.filter(Boolean) : [],
      artwork_commentary_media: Array.isArray(parsed.artwork_commentary_media) ? parsed.artwork_commentary_media.filter(Boolean) : [],
      preview_image: typeof parsed.preview_image === "string" ? parsed.preview_image : ""
    };
  } catch {
    return emptyAdditionalMedia;
  }
};
const isVideoUrl = (url: string) => {
  const fileInfo = getFileInfoFromUrl(url);
  if (!fileInfo?.extension) {
    return false;
  }
  return VIDEO_EXTENSIONS.has(fileInfo.extension.toLowerCase());
};
interface WaveDropAdditionalInfoProps {
  readonly drop: ExtendedDrop;
}
export const WaveDropAdditionalInfo = t0 => {
  const $ = _c(34);
  const {
    drop
  } = t0;
  let aboutArtistValue;
  let commentaryValue;
  let previewImageValue;
  let t1;
  if ($[0] !== drop.metadata) {
    const metadata = drop.metadata ?? [];
    const getMetadataValue = key => metadata.find(item => item.data_key === key)?.data_value?.trim() ?? "";
    let t2;
    if ($[5] !== getMetadataValue) {
      t2 = getMetadataValue(MemesSubmissionAdditionalInfoKey.COMMENTARY);
      $[5] = getMetadataValue;
      $[6] = t2;
    } else {
      t2 = $[6];
    }
    commentaryValue = t2;
    let t3;
    if ($[7] !== getMetadataValue) {
      t3 = getMetadataValue(MemesSubmissionAdditionalInfoKey.ABOUT_ARTIST);
      $[7] = getMetadataValue;
      $[8] = t3;
    } else {
      t3 = $[8];
    }
    aboutArtistValue = t3;
    const additionalMediaEntry = metadata.find(_temp);
    const additionalMedia = parseAdditionalMedia(additionalMediaEntry?.data_value);
    previewImageValue = additionalMedia.preview_image ? parseIpfsUrl(additionalMedia.preview_image) : "";
    t1 = additionalMedia.artwork_commentary_media.filter(_temp2).map(_temp3).filter(_temp4).map(_temp5);
    $[0] = drop.metadata;
    $[1] = aboutArtistValue;
    $[2] = commentaryValue;
    $[3] = previewImageValue;
    $[4] = t1;
  } else {
    aboutArtistValue = $[1];
    commentaryValue = $[2];
    previewImageValue = $[3];
    t1 = $[4];
  }
  const mediaItemsValue = t1;
  let t2;
  if ($[9] !== aboutArtistValue || $[10] !== commentaryValue || $[11] !== mediaItemsValue || $[12] !== previewImageValue) {
    t2 = {
      commentary: commentaryValue,
      aboutArtist: aboutArtistValue,
      previewImage: previewImageValue,
      mediaItems: mediaItemsValue
    };
    $[9] = aboutArtistValue;
    $[10] = commentaryValue;
    $[11] = mediaItemsValue;
    $[12] = previewImageValue;
    $[13] = t2;
  } else {
    t2 = $[13];
  }
  const {
    commentary,
    aboutArtist,
    previewImage,
    mediaItems
  } = t2;
  let t3;
  let t4;
  let t5;
  let t6;
  if ($[14] !== aboutArtist || $[15] !== commentary || $[16] !== mediaItems || $[17] !== previewImage) {
    t6 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const displayedMedia = mediaItems.slice(0, MAX_MEDIA);
      const hasContent = previewImage || displayedMedia.length > 0 || aboutArtist || commentary;
      if (!hasContent) {
        t6 = null;
        break bb0;
      }
      t3 = "tw-space-y-8";
      if ($[22] !== previewImage) {
        t4 = previewImage && <div className="tw-space-y-2"><h3 className="tw-text-base tw-font-semibold tw-text-iron-100">Preview Image</h3><div className="tw-flex tw-justify-center"><div className="tw-relative tw-aspect-[4/3] tw-w-full tw-max-w-2xl tw-overflow-hidden tw-bg-white/[0.02]"><Image src={getScaledImageUri(previewImage, ImageScale.AUTOx600)} alt="Preview image" fill={true} sizes="(min-width: 768px) 400px, 100vw" className="tw-object-contain" /></div></div></div>;
        $[22] = previewImage;
        $[23] = t4;
      } else {
        t4 = $[23];
      }
      t5 = displayedMedia.length > 0 && <div className="tw-space-y-2"><h3 className="tw-text-base tw-font-semibold tw-text-iron-100">Additional Media</h3><div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-3 md:tw-gap-4">{displayedMedia.map(_temp6)}</div></div>;
    }
    $[14] = aboutArtist;
    $[15] = commentary;
    $[16] = mediaItems;
    $[17] = previewImage;
    $[18] = t3;
    $[19] = t4;
    $[20] = t5;
    $[21] = t6;
  } else {
    t3 = $[18];
    t4 = $[19];
    t5 = $[20];
    t6 = $[21];
  }
  if (t6 !== Symbol.for("react.early_return_sentinel")) {
    return t6;
  }
  let t7;
  if ($[24] !== aboutArtist) {
    t7 = aboutArtist && <div className="tw-space-y-2"><h3 className="tw-text-base tw-font-semibold tw-text-iron-100">About the Artist</h3><p className="tw-mb-0 tw-text-sm tw-text-iron-400 tw-leading-relaxed tw-whitespace-pre-wrap">{aboutArtist}</p></div>;
    $[24] = aboutArtist;
    $[25] = t7;
  } else {
    t7 = $[25];
  }
  let t8;
  if ($[26] !== commentary) {
    t8 = commentary && <div className="tw-space-y-2"><h3 className="tw-text-base tw-font-semibold tw-text-iron-100">Artwork Commentary</h3><p className="tw-mb-0 tw-text-sm tw-text-iron-400 tw-leading-relaxed tw-whitespace-pre-wrap">{commentary}</p></div>;
    $[26] = commentary;
    $[27] = t8;
  } else {
    t8 = $[27];
  }
  let t9;
  if ($[28] !== t3 || $[29] !== t4 || $[30] !== t5 || $[31] !== t7 || $[32] !== t8) {
    t9 = <section className={t3}>{t4}{t5}{t7}{t8}</section>;
    $[28] = t3;
    $[29] = t4;
    $[30] = t5;
    $[31] = t7;
    $[32] = t8;
    $[33] = t9;
  } else {
    t9 = $[33];
  }
  return t9;
};
function _temp(item_0) {
  return item_0.data_key === MemesSubmissionAdditionalInfoKey.ADDITIONAL_MEDIA;
}
function _temp2(url) {
  return typeof url === "string" && url.trim().length > 0;
}
function _temp3(url_0) {
  return parseIpfsUrl(url_0);
}
function _temp4(url_1) {
  return url_1.length > 0;
}
function _temp5(url_2) {
  const isVideo = isVideoUrl(url_2);
  const displayUrl = isVideo ? url_2 : getScaledImageUri(url_2, ImageScale.AUTOx600);
  return {
    url: url_2,
    displayUrl,
    isVideo
  };
}
function _temp6(item_1, index) {
  return <div key={`${item_1.url}-${index}`} className={`tw-relative tw-overflow-hidden tw-bg-white/[0.02] ${item_1.isVideo ? "tw-aspect-video sm:tw-col-span-2" : "tw-aspect-[4/3]"}`}>{item_1.isVideo ? <video src={item_1.url} className="tw-h-full tw-w-full tw-object-cover" controls={true} preload="metadata"><track kind="captions" src="data:text/vtt,WEBVTT" srcLang="en" label="Captions" /></video> : <Image src={item_1.displayUrl} alt={`Additional media ${index + 1}`} fill={true} sizes="(min-width: 1024px) 25vw, (min-width: 768px) 33vw, 50vw" className="tw-object-contain" />}</div>;
}